import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/vue'
import { getClient } from '@sentry/vue'
import { Configuration as UsersConfig, UsersApi, AvailabilityApi, WorkingHoursApi } from '@/api/generated-users'
import { Configuration as OrgsConfig, OrganizationsApi, InvitationsApi } from '@/api/generated-organizations'
import { Configuration as RolesConfig, RolesApi } from '@/api/generated-roles'
import { Configuration as TeamsConfig, TeamsApi } from '@/api/generated-teams'
import { Configuration as UserRolesConfig, UserRolesApi } from '@/api/generated-user-roles'
import { Configuration as UserTeamsConfig, UserTeamsApi } from '@/api/generated-user-teams'
import {
  Configuration as ClientsConfig,
  CareCircleApi,
  HuisartsApi,
  ClientActivityApi,
  ClientAiRecapApi,
  ClientAvatarApi,
  ClientCommentsApi,
  ClientFinancieringApi,
  ClientFinancingsApi,
  ClientVerantwoordelijkeGemeenteApi,
  ClientZorgvraagApi,
  ClientsApi,
  FormAssignmentsApi
} from '@/api/generated-clients'
import { Configuration as ZorgtrajectenConfig, ZorgtrajectenApi } from '@/api/generated-zorgtrajecten'
import {
  Configuration as ZorgvraagtyperingConfig,
  ZorgvraagtyperingApi,
  ReferenceDataApi as ZorgvraagtyperingReferenceDataApi
} from '@/api/generated-zorgvraagtypering'
import {
  Configuration as ZorgtrajectVersionedConfig,
  SettingApi,
  BehandelaarApi,
  PrimairBehandelaarApi,
  GgzProfielApi,
  ReferenceDataApi as DsmHoofdgroepReferenceDataApi
} from '@/api/generated-zorgtraject-versioned'
import { Configuration as ContractsConfig, ContractsApi, ReferenceDataApi } from '@/api/generated-contracts'
import { Configuration as SecurityConfig, SecurityApi, OrganizationMfaApi } from '@/api/generated-security'
import { Configuration as LocationsConfig, LocationsApi, RoomsApi } from '@/api/generated-locations'
import { Configuration as AppointmentsConfig, AppointmentsApi, AppointmentStatusApi, AppointmentSeriesApi, AgendaMembersApi } from '@/api/generated-appointments'
import { Configuration as DocumentsConfig, DocumentsApi, DocumentLabelsApi, OrganizationDefaultDocumentsApi } from '@/api/generated-documents'
import { Configuration as TranscriptionsConfig, TranscriptionsApi } from '@/api/generated-transcriptions'
import { Configuration as BillingConfig, BillingLinesApi, BillableAppointmentsApi, BillingBatchesApi, BillingSignalsApi, ClientBillingSummaryApi, GemeenteDeclaratiesApi, GemeenteVerantwoordingApi, InsuranceVerificationApi, JeugdwetBerichtenApi, JeugdwetToewijzingsverzoekenApi, SettingsApi as BillingSettingsApi } from '@/api/generated-billing'
import { Configuration as InvoicesConfig, DebiteurenApi, InvoicesApi } from '@/api/generated-invoices'
import { Configuration as AppointmentTypesConfig, AppointmentTypesApi, ReminderRulesApi } from '@/api/generated-appointment-types'
import { Configuration as ClientStatesConfig, ClientStatesApi, ClientStatePeriodsApi } from '@/api/generated-client-states'
import { Configuration as FormsConfig, QuestionsApi, FormsApi, FormItemsApi, FormPdfApi, FormSubmissionsApi, FormSubmissionAnswersApi, AppointmentFormsApi, AppointmentTypeFormsApi, AnswersApi } from '@/api/generated-forms'
import { Configuration as ClientAuthConfig, ClientAuthApi } from '@/api/generated-client-auth'
import { Configuration as PortalConfig, PortalApi, PortalAppointmentsApi, PortalDocumentsApi, PortalFormsApi, PortalInvoicesApi } from '@/api/generated-portal'
import { Configuration as LettersConfig, LetterheadApi, LetterTemplatesApi, LetterTemplatePlaceholdersApi, LettersApi, ZorgmailGatewayTestsApi } from '@/api/generated-letters'
import { Configuration as ClientGroupsConfig, ClientGroupsApi, ClientGroupMembershipsApi } from '@/api/generated-client-groups'
import { Configuration as SignalsConfig, SignalsApi, SignalDefinitionsApi, SignalSchemaApi, SignalPreviewApi, SignalDraftsApi, SignalSuppressionsApi } from '@/api/generated-signals'
import { Configuration as WhisperConfig, UploadsApi, HealthApi as WhisperHealthApi } from '@/api/generated-whisper'
import { Configuration as AdminConfig, AdminAdminUsersApi, AdminAgreementsApi, AdminAuditApi, AdminAuthApi, AdminDashboardApi, AdminHubspotApi, AdminImportsApi, AdminInsurerReturnsApi, AdminInvitationsApi, AdminSnelstartApi, AdminOrganizationClosureApi, AdminOrganizationsApi, AdminSubscriptionBoekhoudingMailApi, AdminSubscriptionDiscountsApi, AdminSubscriptionsApi, AdminSupportPartnersApi, AdminUsersApi } from '@/api/generated-admin'
import { Configuration as CertificatesConfig, CertificatesApi } from '@/api/generated-certificates'
import { Configuration as AuditConfig, AuditLogsApi } from '@/api/generated-audit'
import { Configuration as AnalyticsConfig, AnalyticsApi } from '@/api/generated-analytics'
import { Configuration as Dsm5DiagnosesConfig, Dsm5DiagnosesApi } from '@/api/generated-dsm5-diagnoses'
import { Configuration as PlatformConfig, ApiKoppelingenApi, AuthApi, SignInApi, NawApi, IntegrationsApi, SnelstartIntegrationApi, UserIntegrationsApi } from '@/api/generated-platform'
import {
  Configuration as ImportsConfig,
  AgendaImportsApi,
  ContractTariffImportsApi,
  ImportApprovalApi,
  ImportAppointmentTypesApi,
  ImportFindingsApi,
  ImportMissingFieldsApi,
  ImportPractitionersApi,
  ImportPreviewApi,
  ImportRunsApi,
  ImportSettingsApi,
  ImportTrajectoryOverlapsApi,
  ImportUploadsApi,
  ImportsApi
} from '@/api/generated-imports'
import { Configuration as SubscriptionsConfig, SubscriptionsApi } from '@/api/generated-subscriptions'
import { Configuration as ZorgMailInboxConfig, ZorgmailInboxApi } from '@/api/generated-zorgmail-inbox'
import type { AuthProvider } from './AuthProvider'
import { config } from '@/config'
import { generateUUID } from '@/utils/uuid'
import { getLoginPath } from '@/utils/domain'
import { handleSessionExpired, isSessionExpiredError } from './sessionExpiry'
import { reportAdminSessionExpired } from './adminSessionExpiry'
import { getApiErrorCode, logUnexpectedFailure } from '@/utils/errorHandling'
import { recordOrganizationReadOnlyRefusal } from '@/composables/useOrganizationReadOnly'
import { endImpersonationIfExpired } from '@/composables/useImpersonation'

/** The backend's `code` on the 401 refusing a behandelaar's Keycloak token. */
const KEYCLOAK_SIGN_IN_REFUSED_CODE = 'keycloak-sign-in-refused'

/**
 * API Service that provides configured API clients with authentication.
 *
 * This service wraps the generated OpenAPI clients and adds:
 * - Base URL configuration
 * - Firebase ID token authentication
 * - Request/response interceptors
 * - Error handling
 */
export class ApiService {
  private axiosInstance: AxiosInstance
  private whisperAxiosInstance: AxiosInstance
  private authProvider: AuthProvider

  constructor(authProvider: AuthProvider, baseURL?: string) {
    this.authProvider = authProvider

    // Create axios instance with base configuration for Kotlin backend
    this.axiosInstance = axios.create({
      baseURL: baseURL || config.apiBaseUrl,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Create separate axios instance for Whisper service
    this.whisperAxiosInstance = axios.create({
      baseURL: config.whisperApiUrl,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Configure both instances with auth interceptors
    this.configureAxiosInstance(this.axiosInstance)
    this.configureAxiosInstance(this.whisperAxiosInstance)
  }

  private configureAxiosInstance(instance: AxiosInstance) {
    // Add request interceptor to include auth token and correlation ID
    instance.interceptors.request.use(
      async (reqConfig) => {
        // Generate correlation ID for end-to-end tracing (ADR-016)
        const correlationId = generateUUID()
        reqConfig.headers['X-Correlation-ID'] = correlationId

        // Only set Sentry tag if Sentry is initialized (avoids issues in E2E tests)
        if (getClient()) {
          Sentry.setTag('correlationId', correlationId)
        }

        // Skip token fetch if this is a 401 retry - the fresh token was already set
        // directly on the request headers by the response interceptor (SCRIVIO-FE-3D fix)
        const isRetry401 = (reqConfig as InternalAxiosRequestConfig & { _retry401?: boolean })._retry401
        if (!isRetry401) {
          // Get the ID token. Minting one fails outright when the refresh
          // token has expired or been revoked — every authenticated route passes
          // through here, so this is where a dead session becomes a sign-out
          // instead of a failed request (Fixes SCRIVIO-FE-8T).
          let token: string | null
          try {
            token = await this.authProvider.getIdToken()
          } catch (tokenError) {
            if (isSessionExpiredError(tokenError)) {
              void handleSessionExpired()
            }
            throw tokenError
          }
          if (token) {
            reqConfig.headers.Authorization = `Bearer ${token}`
          }
        }
        // Let the browser set the correct Content-Type with boundary for FormData
        if (reqConfig.data instanceof FormData) {
          delete reqConfig.headers['Content-Type']
        }
        return reqConfig
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Add response interceptor for error handling with 401 retry logic
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // The organisation write gate refused this write (#4445). Recorded here
        // rather than at the call site: it is true of the whole organisation,
        // not of this action, so it must raise the app-wide banner whatever
        // screen the save came from — including the screens whose catch block
        // does nothing with the error.
        recordOrganizationReadOnlyRefusal(error)

        // Capture 5xx errors to Sentry for monitoring server-side issues
        if (error.response?.status >= 500 && getClient()) {
          const correlationId = error.config?.headers?.['X-Correlation-ID']
          Sentry.captureException(error, {
            tags: {
              'http.status_code': error.response.status,
              'http.url': error.config?.url,
              'http.method': error.config?.method?.toUpperCase(),
              ...(correlationId && { correlationId }),
            },
          })
        }

        // The backend ended an impersonated session at its hour (#4678). Not
        // retried: a refreshed token carries the same start claim and is
        // refused the same way.
        if (endImpersonationIfExpired(error)) {
          return Promise.reject(error)
        }

        // Handle 401 errors with token refresh retry (Fixes SCRIVIO-FE-3D)
        // This handles cases where the token expires mid-session or becomes stale
        if (error.response?.status === 401) {
          // The admin session's hour is up: the token is valid, so refreshing
          // it would only repeat the refusal. The admin guard signs out.
          if (reportAdminSessionExpired(error)) {
            return Promise.reject(error)
          }
          // Keycloak signed this person in, and Scrivio no longer admits them that way — their
          // organisation went back to Firebase. A fresh token says the same, so end the session.
          if (getApiErrorCode(error) === KEYCLOAK_SIGN_IN_REFUSED_CODE) {
            void handleSessionExpired()
            return Promise.reject(error)
          }

          const originalRequest = error.config as InternalAxiosRequestConfig & { _retry401?: boolean }
          const signedIn = this.authProvider.currentSession() !== null

          // Only retry if:
          // 1. User is still authenticated with the auth provider
          // 2. We haven't already retried this request (prevent infinite loops)
          if (signedIn && originalRequest && !originalRequest._retry401) {
            originalRequest._retry401 = true

            try {
              // Force token refresh to get fresh credentials
              const freshToken = await this.authProvider.getIdToken(true)

              // CRITICAL: Directly set the fresh token on the request headers
              // Do NOT rely on the request interceptor to fetch it again, as
              // getIdToken() without force-refresh may return a stale cached token
              // during auth state transitions (race condition fix for SCRIVIO-FE-3D)
              originalRequest.headers.Authorization = `Bearer ${freshToken}`

              // Retry the original request with the explicitly set fresh token
              return instance.request(originalRequest)
            } catch (refreshError) {
              // Token refresh failed - user session is truly invalid
              if (isSessionExpiredError(refreshError)) {
                // Expected end-of-session, not a defect: sign out quietly so it
                // does not get captured as a Sentry error (Fixes SCRIVIO-FE-8T).
                void handleSessionExpired()
              } else {
                logUnexpectedFailure('Token refresh failed during 401 retry:', refreshError)
                window.location.href = getLoginPath()
              }
              return Promise.reject(error)
            }
          }

          // User is not logged in or retry already attempted - redirect to login
          if (!signedIn) {
            window.location.href = getLoginPath()
          }
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Get the configured axios instance for direct API calls.
   * Use this for requests that need custom response types (e.g., blob for PDF downloads).
   */
  getAxios(): AxiosInstance {
    return this.axiosInstance
  }

  /**
   * Get Users API client with authentication
   */
  get users(): UsersApi {
    const config = new UsersConfig()
    return new UsersApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Availability API client (resolved free/busy per user, T5) with authentication
   */
  get availability(): AvailabilityApi {
    const config = new UsersConfig()
    return new AvailabilityApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Working Hours API client with authentication.
   * Covers weekly working-hours patterns and availability exceptions
   * for both the current user (`me`) and other users.
   */
  get workingHours(): WorkingHoursApi {
    const config = new UsersConfig()
    return new WorkingHoursApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Organizations API client with authentication
   */
  get organizations(): OrganizationsApi {
    const config = new OrgsConfig()
    return new OrganizationsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Invitations API client with authentication
   */
  get invitations(): InvitationsApi {
    const config = new OrgsConfig()
    return new InvitationsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Roles API client with authentication
   */
  get roles(): RolesApi {
    const config = new RolesConfig()
    return new RolesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Teams API client with authentication
   */
  get teams(): TeamsApi {
    const config = new TeamsConfig()
    return new TeamsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get User Roles API client with authentication
   */
  get userRoles(): UserRolesApi {
    const config = new UserRolesConfig()
    return new UserRolesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get User Teams API client with authentication
   */
  get userTeams(): UserTeamsApi {
    const config = new UserTeamsConfig()
    return new UserTeamsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Clients API client with authentication
   */
  get clients(): ClientsApi {
    const config = new ClientsConfig()
    return new ClientsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Care Circle API client with authentication
   */
  get careCircle(): CareCircleApi {
    const config = new ClientsConfig()
    return new CareCircleApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Huisarts API client with authentication.
   *
   * Reads the cliënt's huisarts as a correspondence recipient, and records who it is (#4117).
   * A huisarts is still created and edited through `careCircle` — this is not a second store.
   */
  get huisarts(): HuisartsApi {
    const config = new ClientsConfig()
    return new HuisartsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Activity API client with authentication
   */
  get clientActivity(): ClientActivityApi {
    const config = new ClientsConfig()
    return new ClientActivityApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client AI Recap API client with authentication
   */
  get clientAiRecap(): ClientAiRecapApi {
    const config = new ClientsConfig()
    return new ClientAiRecapApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Avatar API client with authentication
   */
  get clientAvatar(): ClientAvatarApi {
    const config = new ClientsConfig()
    return new ClientAvatarApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Comments API client with authentication
   */
  get clientComments(): ClientCommentsApi {
    const config = new ClientsConfig()
    return new ClientCommentsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Financiering API client with authentication
   */
  get clientFinanciering(): ClientFinancieringApi {
    const config = new ClientsConfig()
    return new ClientFinancieringApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Financings API client with authentication.
   *
   * The financings per *regime* (ADR-030) — a different record from
   * `clientFinanciering` above, which owns the Zvw/eigen-bijdrage detail and the
   * insurance policies.
   */
  get clientFinancings(): ClientFinancingsApi {
    const config = new ClientsConfig()
    return new ClientFinancingsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the API client for a cliënt's verantwoordelijke gemeente per regime (Jeugdwet, Wmo).
   */
  get clientVerantwoordelijkeGemeenten(): ClientVerantwoordelijkeGemeenteApi {
    const config = new ClientsConfig()
    return new ClientVerantwoordelijkeGemeenteApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Zorgvraag API client with authentication
   */
  get clientZorgvraag(): ClientZorgvraagApi {
    const config = new ClientsConfig()
    return new ClientZorgvraagApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Form Assignments API client with authentication
   */
  get formAssignments(): FormAssignmentsApi {
    const config = new ClientsConfig()
    return new FormAssignmentsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Zorgtrajecten API client with authentication
   */
  get zorgtrajecten(): ZorgtrajectenApi {
    const config = new ZorgtrajectenConfig()
    return new ZorgtrajectenApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Zorgvraagtypering API client with authentication
   */
  get zorgvraagtypering(): ZorgvraagtyperingApi {
    const config = new ZorgvraagtyperingConfig()
    return new ZorgvraagtyperingApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Zorgvraagtypering Reference Data API client with authentication
   */
  get zorgvraagtyperingReferenceData(): ZorgvraagtyperingReferenceDataApi {
    const config = new ZorgvraagtyperingConfig()
    return new ZorgvraagtyperingReferenceDataApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get DSM hoofdgroep (CL0005-NZA) Reference Data API client with authentication.
   * Lives in the zorgtraject-versioned spec since the GGZ-profiel consolidation (#3594).
   */
  get dsmHoofdgroepReferenceData(): DsmHoofdgroepReferenceDataApi {
    const config = new ZorgtrajectVersionedConfig()
    return new DsmHoofdgroepReferenceDataApi(config, undefined, this.axiosInstance)
  }

  get settingApi(): SettingApi {
    const config = new ZorgtrajectVersionedConfig()
    return new SettingApi(config, undefined, this.axiosInstance)
  }

  get behandelaarApi(): BehandelaarApi {
    const config = new ZorgtrajectVersionedConfig()
    return new BehandelaarApi(config, undefined, this.axiosInstance)
  }

  get primairBehandelaarApi(): PrimairBehandelaarApi {
    const config = new ZorgtrajectVersionedConfig()
    return new PrimairBehandelaarApi(config, undefined, this.axiosInstance)
  }

  get ggzProfielApi(): GgzProfielApi {
    const config = new ZorgtrajectVersionedConfig()
    return new GgzProfielApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Contracts API client with authentication
   */
  get contracts(): ContractsApi {
    const config = new ContractsConfig()
    return new ContractsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Reference Data API client with authentication
   */
  get referenceData(): ReferenceDataApi {
    const config = new ContractsConfig()
    return new ReferenceDataApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Security API client with authentication
   */
  get security(): SecurityApi {
    const config = new SecurityConfig()
    return new SecurityApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Organization MFA API client with authentication
   */
  get organizationMfa(): OrganizationMfaApi {
    const config = new SecurityConfig()
    return new OrganizationMfaApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Locations API client with authentication
   */
  get locations(): LocationsApi {
    const config = new LocationsConfig()
    return new LocationsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Rooms API client with authentication
   */
  get rooms(): RoomsApi {
    const config = new LocationsConfig()
    return new RoomsApi(config, undefined, this.axiosInstance)
  }

  get appointments(): AppointmentsApi {
    const config = new AppointmentsConfig()
    return new AppointmentsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Appointment Status API client (cancel, no-show, complete, revert)
   */
  get appointmentStatus(): AppointmentStatusApi {
    const config = new AppointmentsConfig()
    return new AppointmentStatusApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Appointment Series API client (recurring appointments)
   */
  get appointmentSeries(): AppointmentSeriesApi {
    const config = new AppointmentsConfig()
    return new AppointmentSeriesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Agenda Members API client (selectable and visible organization members)
   */
  get agendaMembers(): AgendaMembersApi {
    const config = new AppointmentsConfig()
    return new AgendaMembersApi(config, undefined, this.axiosInstance)
  }

  get documents(): DocumentsApi {
    const config = new DocumentsConfig()
    return new DocumentsApi(config, undefined, this.axiosInstance)
  }

  get documentLabels(): DocumentLabelsApi {
    const config = new DocumentsConfig()
    return new DocumentLabelsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Organisation-wide default documents — the configuration, not the copies in dossiers
   * (#4342, epic #4010). Served by the documents spec, so it takes that spec's configuration.
   */
  get organizationDefaultDocuments(): OrganizationDefaultDocumentsApi {
    const config = new DocumentsConfig()
    return new OrganizationDefaultDocumentsApi(config, undefined, this.axiosInstance)
  }

  get transcriptions(): TranscriptionsApi {
    const config = new TranscriptionsConfig()
    return new TranscriptionsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Billing Lines API client with authentication
   */
  get billingLines(): BillingLinesApi {
    const config = new BillingConfig()
    return new BillingLinesApi(config, undefined, this.axiosInstance)
  }

  get billableAppointments(): BillableAppointmentsApi {
    const config = new BillingConfig()
    return new BillableAppointmentsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Billing Batches API client with authentication
   */
  get billingBatches(): BillingBatchesApi {
    const config = new BillingConfig()
    return new BillingBatchesApi(config, undefined, this.axiosInstance)
  }

  /**
   * The gemeente prestaties still to be declared, per gemeente — the Gemeente tab (#4821).
   */
  get billingGemeenteDeclaraties(): GemeenteDeclaratiesApi {
    const config = new BillingConfig()
    return new GemeenteDeclaratiesApi(config, undefined, this.axiosInstance)
  }

  /**
   * The iJw message log: one bericht viewed or downloaded as exchanged (#4704).
   */
  get jeugdwetBerichten(): JeugdwetBerichtenApi {
    const config = new BillingConfig()
    return new JeugdwetBerichtenApi(config, undefined, this.axiosInstance)
  }

  /**
   * Requesting a toewijzing (JW315) from a Jeugdwet zorgtraject (#4810).
   */
  get jeugdwetToewijzingsverzoeken(): JeugdwetToewijzingsverzoekenApi {
    const config = new BillingConfig()
    return new JeugdwetToewijzingsverzoekenApi(config, undefined, this.axiosInstance)
  }

  /**
   * The yearly verantwoording per gemeente, read from the declared lines (#4824).
   */
  get gemeenteVerantwoording(): GemeenteVerantwoordingApi {
    const config = new BillingConfig()
    return new GemeenteVerantwoordingApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Billing Signals API client with authentication.
   * Answers "what blocks the declaratie queue", aggregated per cause (#3815).
   */
  get billingSignals(): BillingSignalsApi {
    const config = new BillingConfig()
    return new BillingSignalsApi(config, undefined, this.axiosInstance)
  }

  get clientBillingSummary(): ClientBillingSummaryApi {
    const config = new BillingConfig()
    return new ClientBillingSummaryApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Billing Settings API client with authentication.
   * This provides access to treatment settings (behandelsettings) reference data.
   */
  get billingSettings(): BillingSettingsApi {
    const config = new BillingConfig()
    return new BillingSettingsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Invoices API client with authentication
   */
  get invoices(): InvoicesApi {
    const config = new InvoicesConfig()
    return new InvoicesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Debiteuren API client with authentication
   */
  get debiteuren(): DebiteurenApi {
    const config = new InvoicesConfig()
    return new DebiteurenApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Appointment Types API client with authentication
   */
  get appointmentTypes(): AppointmentTypesApi {
    const config = new AppointmentTypesConfig()
    return new AppointmentTypesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Reminder Rules API client with authentication
   */
  get reminderRules(): ReminderRulesApi {
    const config = new AppointmentTypesConfig()
    return new ReminderRulesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client States API client with authentication
   */
  get clientStates(): ClientStatesApi {
    const config = new ClientStatesConfig()
    return new ClientStatesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client State Periods API client with authentication
   */
  get clientStatePeriods(): ClientStatePeriodsApi {
    const config = new ClientStatesConfig()
    return new ClientStatePeriodsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Questions API client with authentication
   */
  get questions(): QuestionsApi {
    const config = new FormsConfig()
    return new QuestionsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Forms API client with authentication
   */
  get forms(): FormsApi {
    const config = new FormsConfig()
    return new FormsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Form Items API client with authentication
   */
  get formItems(): FormItemsApi {
    const config = new FormsConfig()
    return new FormItemsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Form PDF API client for printable blank forms and filled submissions
   */
  get formPdf(): FormPdfApi {
    const config = new FormsConfig()
    return new FormPdfApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Form Submissions API client with authentication
   */
  get formSubmissions(): FormSubmissionsApi {
    const config = new FormsConfig()
    return new FormSubmissionsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Form Submission Answers API client (answers, AI suggestions, review)
   */
  get formSubmissionAnswers(): FormSubmissionAnswersApi {
    const config = new FormsConfig()
    return new FormSubmissionAnswersApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Appointment Forms API client with authentication
   */
  get appointmentForms(): AppointmentFormsApi {
    const config = new FormsConfig()
    return new AppointmentFormsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Appointment Type Forms API client with authentication
   */
  get appointmentTypeForms(): AppointmentTypeFormsApi {
    const config = new FormsConfig()
    return new AppointmentTypeFormsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Answers API client with authentication
   */
  get answers(): AnswersApi {
    const config = new FormsConfig()
    return new AnswersApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Auth API client for client portal authentication
   */
  get clientAuth(): ClientAuthApi {
    const config = new ClientAuthConfig()
    return new ClientAuthApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Portal API client for client portal self-service
   */
  get portal(): PortalApi {
    const config = new PortalConfig()
    return new PortalApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Portal Appointments API client for client portal appointment viewing
   */
  get portalAppointments(): PortalAppointmentsApi {
    const config = new PortalConfig()
    return new PortalAppointmentsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Portal Documents API client for client portal document access
   */
  get portalDocuments(): PortalDocumentsApi {
    const config = new PortalConfig()
    return new PortalDocumentsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Portal Forms API client for client portal form filling
   */
  get portalForms(): PortalFormsApi {
    const config = new PortalConfig()
    return new PortalFormsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Portal Invoices API client for client portal invoice access
   */
  get portalInvoices(): PortalInvoicesApi {
    const config = new PortalConfig()
    return new PortalInvoicesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Letter Templates API client with authentication
   */
  get letterTemplates(): LetterTemplatesApi {
    const config = new LettersConfig()
    return new LetterTemplatesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Letter Template Placeholders API client with authentication
   */
  get letterTemplatePlaceholders(): LetterTemplatePlaceholdersApi {
    const config = new LettersConfig()
    return new LetterTemplatePlaceholdersApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Letters API client with authentication
   */
  get letters(): LettersApi {
    const config = new LettersConfig()
    return new LettersApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the organisation letterhead API client with authentication
   */
  get letterhead(): LetterheadApi {
    const config = new LettersConfig()
    return new LetterheadApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the ZorgMail gateway-test API client with authentication (#4710)
   */
  get zorgMailGatewayTests(): ZorgmailGatewayTestsApi {
    const config = new LettersConfig()
    return new ZorgmailGatewayTestsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Groups API client with authentication
   */
  get clientGroups(): ClientGroupsApi {
    const config = new ClientGroupsConfig()
    return new ClientGroupsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Client Group Memberships API client with authentication
   */
  get clientGroupMemberships(): ClientGroupMembershipsApi {
    const config = new ClientGroupsConfig()
    return new ClientGroupMembershipsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Signals API client with authentication
   */
  get signals(): SignalsApi {
    const config = new SignalsConfig()
    return new SignalsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Signal Definitions API client with authentication
   */
  get signalDefinitions(): SignalDefinitionsApi {
    const config = new SignalsConfig()
    return new SignalDefinitionsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Signal Schema API client with authentication
   */
  get signalSchema(): SignalSchemaApi {
    const config = new SignalsConfig()
    return new SignalSchemaApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Signal Preview API client with authentication.
   * Read-only preview of a candidate signal rule (ADR-034).
   */
  get signalPreview(): SignalPreviewApi {
    const config = new SignalsConfig()
    return new SignalPreviewApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Signal Drafts API client with authentication.
   * Natural-language signal authoring: generate, save-inactive, discard (ADR-034).
   */
  get signalDrafts(): SignalDraftsApi {
    const config = new SignalsConfig()
    return new SignalDraftsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Signal Suppressions API client with authentication.
   * Putting one signal aside, org-wide, with the actor and reason recorded (#4389).
   */
  get signalSuppressions(): SignalSuppressionsApi {
    const config = new SignalsConfig()
    return new SignalSuppressionsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Whisper Uploads API client with authentication.
   * Uses whisperAxiosInstance pointing to whisper.scrivio.nl
   */
  get whisperUploads(): UploadsApi {
    const whisperConfig = new WhisperConfig()
    return new UploadsApi(whisperConfig, undefined, this.whisperAxiosInstance)
  }

  /**
   * Get Whisper Health API client with authentication.
   * Uses whisperAxiosInstance pointing to whisper.scrivio.nl
   */
  get whisperHealth(): WhisperHealthApi {
    const whisperConfig = new WhisperConfig()
    return new WhisperHealthApi(whisperConfig, undefined, this.whisperAxiosInstance)
  }

  /**
   * Get Admin Auth API client with authentication
   */
  get adminAuth(): AdminAuthApi {
    const adminConfig = new AdminConfig()
    return new AdminAuthApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Dashboard API client with authentication
   */
  get adminDashboard(): AdminDashboardApi {
    const adminConfig = new AdminConfig()
    return new AdminDashboardApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Organizations API client with authentication
   */
  get adminOrganizations(): AdminOrganizationsApi {
    const adminConfig = new AdminConfig()
    return new AdminOrganizationsApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Agreements API client with authentication — what a practice agreed to (#4492)
   */
  get adminAgreements(): AdminAgreementsApi {
    const adminConfig = new AdminConfig()
    return new AdminAgreementsApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Users API client with authentication
   */
  get adminUsers(): AdminUsersApi {
    const adminConfig = new AdminConfig()
    return new AdminUsersApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Audit Log API client with authentication
   */
  get adminAuditLog(): AdminAuditApi {
    const adminConfig = new AdminConfig()
    return new AdminAuditApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Admin-Users API client with authentication (platform admins and support agents)
   */
  get adminAdminUsers(): AdminAdminUsersApi {
    const adminConfig = new AdminConfig()
    return new AdminAdminUsersApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Support Partners API client with authentication
   */
  get adminSupportPartners(): AdminSupportPartnersApi {
    const adminConfig = new AdminConfig()
    return new AdminSupportPartnersApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Invitations API client for the public invitee-side accept flow.
   *
   * Both endpoints are unauthenticated (the invitation token is the capability),
   * but they go through the shared axios instance so an authenticated invitee —
   * who has just self-signed-up — automatically carries their token on accept.
   */
  get adminInvitations(): AdminInvitationsApi {
    const adminConfig = new AdminConfig()
    return new AdminInvitationsApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Imports API client (ZPM GGZ workbook validation on behalf of an organization)
   */
  get adminImports(): AdminImportsApi {
    const adminConfig = new AdminConfig()
    return new AdminImportsApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Insurer Returns API client: re-applying the GDS802 retourberichten a customer
   * organization already imported (#4511). A repair operation, not the customer's own import.
   */
  get adminInsurerReturns(): AdminInsurerReturnsApi {
    const adminConfig = new AdminConfig()
    return new AdminInsurerReturnsApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin SnelStart API client: the SnelStart subscription key Scrivio holds for one practice's
   * maatwerk connection (#4725). Scrivio's own credential, never the practice's.
   */
  get adminSnelStart(): AdminSnelstartApi {
    const adminConfig = new AdminConfig()
    return new AdminSnelstartApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin HubSpot API client: linking organisations to the companies in Scrivio's HubSpot
   * (#4836, ADR-046).
   */
  get adminHubSpot(): AdminHubspotApi {
    const adminConfig = new AdminConfig()
    return new AdminHubspotApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Organization Closure API client: closing an organization that has ended or was never
   * billed, keeping all its data.
   */
  get adminOrganizationClosure(): AdminOrganizationClosureApi {
    const adminConfig = new AdminConfig()
    return new AdminOrganizationClosureApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Subscriptions API client: Scrivio's own customers — what every practice's
   * subscription adds up to (ADR-042). Not the practice's billing, which runs the other way.
   */
  get adminSubscriptions(): AdminSubscriptionsApi {
    const adminConfig = new AdminConfig()
    return new AdminSubscriptionsApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Subscription Boekhouding Mail API client: the bookkeeping inbox every issued
   * subscription invoice is also mailed to — Scrivio's own, one for the platform.
   */
  get adminSubscriptionBoekhoudingMail(): AdminSubscriptionBoekhoudingMailApi {
    const adminConfig = new AdminConfig()
    return new AdminSubscriptionBoekhoudingMailApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Admin Subscription Discounts API client: granting and revoking a dated discount on one
   * practice's subscription (#4476).
   */
  get adminSubscriptionDiscounts(): AdminSubscriptionDiscountsApi {
    const adminConfig = new AdminConfig()
    return new AdminSubscriptionDiscountsApi(adminConfig, undefined, this.axiosInstance)
  }

  /**
   * Get Certificates API client with authentication
   */
  get certificates(): CertificatesApi {
    const config = new CertificatesConfig()
    return new CertificatesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Insurance Verification API client with authentication
   */
  get insurance(): InsuranceVerificationApi {
    const config = new BillingConfig()
    return new InsuranceVerificationApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Audit Logs API client with authentication
   */
  get auditLogs(): AuditLogsApi {
    const config = new AuditConfig()
    return new AuditLogsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Analytics API client with authentication
   */
  get analytics(): AnalyticsApi {
    const config = new AnalyticsConfig()
    return new AnalyticsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get DSM-5 Diagnoses API client with authentication
   */
  get dsm5Diagnoses(): Dsm5DiagnosesApi {
    const config = new Dsm5DiagnosesConfig()
    return new Dsm5DiagnosesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Imports API client (ZPM GGZ workbook validation against the caller's own org).
   *
   * Currently unused: the admin-portal flow uses {@link adminImports} instead. Kept
   * wired because the org-scoped endpoint is reserved for a future customer
   * self-service flow on app.scrivio.nl (#3018).
   */
  get imports(): ImportsApi {
    const config = new ImportsConfig()
    return new ImportsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the Subscriptions API client: what the practice pays *Scrivio* — its plan, seats,
   * period and discounts (ADR-042).
   *
   * Not to be confused with {@link billing} or {@link invoices}, which are the practice's own
   * declarations and notas to cliënten and insurers. Same English word, opposite direction of
   * money.
   */
  get subscriptions(): SubscriptionsApi {
    const config = new SubscriptionsConfig()
    return new SubscriptionsApi(config, undefined, this.axiosInstance)
  }

  /** The practice's ZorgMail Secure e-mail inbox: folders, messages, attachments and read state (ADR-050). */
  get zorgMailInbox(): ZorgmailInboxApi {
    const config = new ZorgMailInboxConfig()
    return new ZorgmailInboxApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Agenda Imports API client (agenda planning workbook validation and
   * commit against the caller's own org). Unlike the ZPM import, this one is
   * driven by the practice itself, so there is no admin-scoped counterpart.
   */
  get agendaImports(): AgendaImportsApi {
    const config = new ImportsConfig()
    return new AgendaImportsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the import upload API client: it opens an EPD-dump run and answers with
   * a signed URL for its archive (#3771). The archive itself never comes through
   * here — it is gigabytes, and goes straight to Cloud Storage — so this client
   * is the only part of that import the API sees.
   */
  get importUploads(): ImportUploadsApi {
    const config = new ImportsConfig()
    return new ImportUploadsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the import run API client: which EPD delivery this organization is
   * still busy with (#3943). The archive is unpacked and read on the server, so
   * the run outlives the tab that started it — and the run id lived nowhere
   * else, which is what made a closed upload screen unreachable.
   */
  get importRuns(): ImportRunsApi {
    const config = new ImportsConfig()
    return new ImportRunsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the import preview API client — what a staged run will do, per entity
   * type and per row (#3774, #3775). One client for every import: what a run
   * staged is the same kind of thing whatever produced it (ADR-039).
   */
  get importPreview(): ImportPreviewApi {
    const config = new ImportsConfig()
    return new ImportPreviewApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the import findings API client — what a run's rules found, per cause and
   * per cliënt, plus the CSV a practice sends its EPD vendor (#4053). Separate
   * from {@link importPreview} because it answers a different question about the
   * same run: the preview says what the delivery will do, this says what is
   * wrong with it.
   */
  get importFindings(): ImportFindingsApi {
    const config = new ImportsConfig()
    return new ImportFindingsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the import approval API client — the two answers a preview can be given
   * (#3776). Separate from {@link importPreview} because reading a preview and
   * deciding one are separately permissioned.
   */
  get importApproval(): ImportApprovalApi {
    const config = new ImportsConfig()
    return new ImportApprovalApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the Import Practitioners API client: the practitioner names a staged
   * import could not resolve, and the practice's answer for each of them
   * (#3778). One client for every import, as the preview is — what a run staged
   * is the same kind of thing whatever produced it.
   */
  get importPractitioners(): ImportPractitionersApi {
    const config = new ImportsConfig()
    return new ImportPractitionersApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the contract tariff import API client: loading an insurer's
   * per-prestatiecode tariff sheet onto one contract (#4031). Its own client
   * rather than a second method on {@link agendaImports} because the two are
   * separately permissioned and aimed at different things — a planning at the
   * agenda, a tariff list at one contract's pricing.
   */
  get contractTariffImports(): ContractTariffImportsApi {
    const config = new ImportsConfig()
    return new ContractTariffImportsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the Import Appointment Types API client: the activity types a delivery
   * uses, and which of the organization's own appointment types each of them is
   * (#3784, #3811). Separate from {@link importPractitioners} because they are two
   * questions about one run, answered by two endpoints — and this one decides what
   * the imported sessions will be *worth*, since an appointment type carries the
   * billing category.
   */
  get importAppointmentTypes(): ImportAppointmentTypesApi {
    const config = new ImportsConfig()
    return new ImportAppointmentTypesApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the Import Settings API client: the setting codes a delivery's
   * zorgtrajecten carry, and which behandelsetting each one is (#4769). A
   * question beside {@link importAppointmentTypes} with the same stake — the
   * behandelsetting decides every prestatiecode on the zorgtraject.
   */
  get importSettings(): ImportSettingsApi {
    const config = new ImportsConfig()
    return new ImportSettingsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the Import Missing Fields API client: the required fields a delivery
   * left empty, and the values a practice supplies for them (#4052). A third
   * question about one run beside {@link importPractitioners} and
   * {@link importAppointmentTypes}, and the only one a corrected re-delivery
   * cannot answer — the source system does not hold the value at all.
   */
  get importMissingFields(): ImportMissingFieldsApi {
    const config = new ImportsConfig()
    return new ImportMissingFieldsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the Import Trajectory Overlaps API client: the zorgtrajecten a delivery
   * would close, and the practice's answer about each of them (#4356). A fourth
   * question about one run beside {@link importPractitioners},
   * {@link importAppointmentTypes} and {@link importMissingFields}, and the only
   * one whose answer a revert cannot take back.
   */
  get importTrajectoryOverlaps(): ImportTrajectoryOverlapsApi {
    const config = new ImportsConfig()
    return new ImportTrajectoryOverlapsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the account-recovery API client. Its only endpoint is public — the
   * caller is by definition not signed in — so no token is attached.
   */
  get auth(): AuthApi {
    const config = new PlatformConfig()
    return new AuthApi(config, undefined, this.axiosInstance)
  }

  /** Identifier-first sign-in: which provider an address signs in with. Public, like {@link auth}. */
  get signIn(): SignInApi {
    const config = new PlatformConfig()
    return new SignInApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get NAW lookup API client (SBV-Z integration via the platform service)
   */
  get naw(): NawApi {
    const config = new PlatformConfig()
    return new NawApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get Integrations API client (per-organization external integrations, e.g. Zivver)
   */
  get integrations(): IntegrationsApi {
    const config = new PlatformConfig()
    return new IntegrationsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get SnelStart Integration API client: the practice's connection to its SnelStart boekhouding
   * and the grootboekrekening its verzekeraarfacturen are booked on (#4725)
   */
  get snelStartIntegration(): SnelstartIntegrationApi {
    const config = new PlatformConfig()
    return new SnelstartIntegrationApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get API-koppelingen client: the credentials a practice issues to its integrations (ADR-045)
   */
  get apiKoppelingen(): ApiKoppelingenApi {
    const config = new PlatformConfig()
    return new ApiKoppelingenApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get User Integrations API client (current user's per-user integration
   * settings, e.g. Zivver enablement)
   */
  get userIntegrations(): UserIntegrationsApi {
    const config = new PlatformConfig()
    return new UserIntegrationsApi(config, undefined, this.axiosInstance)
  }

  /**
   * Get the axios instance for custom requests
   */
  get axios(): AxiosInstance {
    return this.axiosInstance
  }
}
